/** Premium settings use separate entitlement, billing, and purchase permissions. */
import apiClient from './api-client.js';
import { premiumRefundRequests, premiumHistoryProviders } from './premium-refund-requests.js';
import { premiumBillingControls } from './premium-billing-controls.js';
import { getJwtToken } from './jwtTokenManager.js';
import { getApiToken } from './tokenUtils.js';
import { createTimeoutSignal } from './timeout-signal.js';
import { premiumBillingFormat } from './premium-billing-format.js';

const PROVIDER_APPLE = 'apple';
const PROVIDER_GOOGLE = 'google';

const REQUEST_TIMEOUT_MS = 15000;

/**
 * This permanent schedule waits 15 seconds between four bounded requests.
 * It prevents the settings page from keeping an open polling loop.
 */
const CHECKOUT_RETRY_DELAYS_MS = [1000, 2000, 4000, 8000];

/**
 * How long to wait for the portal navigation to commit before deciding it was
 * abandoned. Comfortably longer than a redirect to a Stripe-hosted page, so a
 * navigation that is merely slow is never mistaken for one that is not coming.
 */
const NAVIGATION_GRACE_MS = 20000;

/**
 * Bounds a promise that carries no abort signal of its own, so a dependency that
 * hangs surfaces as an error the card can render rather than an endless spinner.
 */
function withTimeout(promise, ms, label) {
    let timer;
    const deadline = new Promise((_resolve, reject) => {
        timer = setTimeout(() => reject(new Error(`${label} timed out`)), ms);
    });

    return Promise.race([promise, deadline]).finally(() => clearTimeout(timer));
}

/** Read only the two return values that our Stripe checkout URLs emit. */
function checkoutResult() {
    const current = new URL(window.location.href);
    const result = current.searchParams.get('checkout');

    return result === 'success' || result === 'cancelled' ? result : null;
}

function clearCheckoutResult() {
    const current = new URL(window.location.href);
    current.searchParams.delete('checkout');
    window.history.replaceState(
        window.history.state,
        '',
        current.pathname + current.search + current.hash,
    );
}

function wait(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * location.assign() executes a `javascript:` URL in our own origin, so the
 * target is required to be https.
 *
 * The host is deliberately not pinned to stripe.com. The URL is minted by our
 * own server from the Stripe SDK and never touches client input, so there is no
 * attack for a host pin to remove, while pinning would strand every subscriber
 * the day the Stripe account enables a custom billing-portal domain.
 */
function portalUrl(value) {
    if (typeof value !== 'string' || value === '') {
        return null;
    }

    let target;
    try {
        target = new URL(value, window.location.origin);
    } catch {
        return null;
    }

    return target.protocol === 'https:' ? target.href : null;
}

/** The body is read inside the timeout, so a stalled body cannot hang the card forever. */
async function readJson(endpoint, options = {}, expectedAccountId, checkAccount) {
    const timeout = createTimeoutSignal(REQUEST_TIMEOUT_MS);

    try {
        if (!checkAccount()) throw new Error('Account changed');
        const response = await apiClient.request(endpoint, { ...options, expectedAccountId, signal: timeout.signal });
        if (!response.ok) {
            const body = await response.json().catch(() => null);
            const error = new Error(`${endpoint} responded ${response.status}`);
            error.code = body?.error?.code;
            error.displayMessage = typeof body?.error?.message === 'string' ? body.error.message : '';
            error.status = response.status;
            throw error;
        }

        const result = await response.json();
        if (!checkAccount()) throw new Error('Account changed');
        return result;
    } catch (error) {
        checkAccount();
        throw error;
    } finally {
        timeout.clear();
    }
}

export function premiumSubscription(strings = {}) {
    const messages = {
        loading: strings.loading || '',
        renewsOn: strings.renewsOn || '',
        expiresOn: strings.expiresOn || '',
        appStore: strings.appStore || '',
        playStore: strings.playStore || '',
        granted: strings.granted || '',
        inactive: strings.inactive || '',
        loadError: strings.loadError || '',
        manageError: strings.manageError || '',
        checkoutSuccess: strings.checkoutSuccess || '',
        checkoutPending: strings.checkoutPending || '',
        offersError: strings.offersError || '',
        checkoutError: strings.checkoutError || '',
        reconciliation: strings.reconciliation || '',
        monthly: strings.monthly || '',
        annual: strings.annual || '',
        billing: strings.billing || {},
        accessUntil: strings.accessUntil || '',
        refundStatuses: strings.refundStatuses || {},
        refundReasons: strings.refundReasons || {},
        recorded: strings.recorded || '',
        resumed: strings.resumed || '',
        canceled: strings.canceled || '',
        changed: strings.changed || '',
        refused: strings.refused || '',
        billingGrace: strings.billingGrace || '',
        billingGraceUntil: strings.billingGraceUntil || '',
        billingRetry: strings.billingRetry || '',
    };

    const accountId = document.querySelector('meta[name="user-id"]')?.getAttribute('content') || '';
    let component;
    const checkAccount = () => {
        if (accountId && String(getJwtToken()?.account?.userId || '') === accountId) return true;
        component.resetSubscription();
        component.billing.confirmation = null;
        component.billing.requestToRetry = null;
        component.billing.notice = '';
        component.refunds.requestToRetry = null;
        component.refunds.payments = [];
        component.refunds.receipts = [];
        component.refunds.selectedPayment = '';
        component.refunds.message = '';
        component.refunds.reason = 'other';
        component.refunds.morePayments = false;
        component.refunds.moreReceipts = false;
        component.refunds.opened = false;
        component.errorMessage = messages.loadError;
        return false;
    };
    const request = (endpoint, options) => readJson(endpoint, options, accountId, checkAccount);
    component = {
        ...premiumBillingFormat(messages),
        billing: premiumBillingControls(request, messages, checkAccount, access => {
            if (!access) return;
            component.enabled = access.enabled === true;
            component.accessDate = component.formatDate(access.valid_until);
        }, accountId),
        refunds: premiumRefundRequests(request, messages, checkAccount, accountId),
        subscriptionLoadingText: messages.loading,
        loading: true,
        working: false,
        enabled: false,
        planName: '',
        provider: null,
        canManageBilling: false,
        billingProvider: null,
        billingIssue: null,
        canPurchase: false,
        refundStoreProviders: [],
        offers: [],
        offersLoading: false,
        offersError: '',
        needsSupport: false,
        autoRenew: false,
        renewalDate: '',
        accessDate: '',
        subscriptionDate: '',
        get billingStatus() { return this.billing.state?.billing || null; },
        errorMessage: '',
        checkoutCompleted: false,

        async init() {
            component = this;
            const result = checkoutResult();
            this.checkoutCompleted = result === 'success';
            if (result === 'cancelled') {
                clearCheckoutResult();
            }

            await this.loadStatus();
            if (this.checkoutCompleted && !this.enabled) {
                await this.refreshAfterCheckout();
            }
            if (this.checkoutCompleted && this.enabled) {
                clearCheckoutResult();
            }

            window.addEventListener('pageshow', async (event) => {
                this.working = false;

                // A back/forward-cache restore does not re-run this component,
                // so without a re-read the card would still assert "Active" and
                // a renewal date to a subscriber who just cancelled in the
                // portal and pressed Back. The re-read is silent so the card
                // does not collapse back to its loading row on a Back.
                if (event.persisted) {
                    this.checkoutCompleted = checkoutResult() === 'success';
                    await this.loadStatus({ silent: true });
                    if (this.checkoutCompleted && this.enabled) {
                        clearCheckoutResult();
                    }
                }
            });
        },

        /** Re-read status while Stripe's webhook updates the entitlement row. */
        async refreshAfterCheckout() {
            for (const delay of CHECKOUT_RETRY_DELAYS_MS) {
                await wait(delay);
                await this.loadStatus({ silent: true, preserveOnError: true });
                if (this.enabled) {
                    return;
                }
            }
        },

        /** Clears every claim the card makes about the subscription. */
        resetSubscription() {
            this.enabled = false;
            this.planName = '';
            this.provider = null;
            this.canManageBilling = false;
            this.billingProvider = null;
            this.billingIssue = null;
            this.canPurchase = false;
            this.refundStoreProviders = [];
            this.offers = [];
            this.autoRenew = false;
            this.renewalDate = '';
            this.accessDate = '';
            this.subscriptionDate = '';
            this.billing.state = null;
        },

        async loadStatus({ silent = false, preserveOnError = false } = {}) {
            if (!silent) {
                this.loading = true;
            }

            try {
                // ApiClient reads the JWT synchronously and does not retry a 401
                // that carried no token, while the token is minted asynchronously
                // in the same turn this first runs. Without awaiting it, a cold
                // token store shows a subscriber a load error on first paint.
                // The mint carries no abort signal of its own, so it is bounded
                // here: it is the first thing that can block, and an unbounded
                // one would hang the card on its spinner with no way out.
                await withTimeout(getApiToken(), REQUEST_TIMEOUT_MS, 'api token');

                const status = await request('/plus-status', { method: 'GET' });

                this.errorMessage = '';
                this.enabled = status.enabled === true;
                this.planName = status.display_name || '';
                this.provider = status.provider || null;
                this.billingProvider = status.billing_provider || null;
                this.billingIssue = [PROVIDER_APPLE, PROVIDER_GOOGLE].includes(this.billingProvider)
                    && ['grace', 'retry'].includes(status.billing_issue) ? status.billing_issue : null;
                this.canPurchase = status.can_purchase_web === true;
                this.refundStoreProviders = premiumHistoryProviders(status, status.billing_provider);
                this.canManageBilling = status.can_manage_billing === true;
                this.autoRenew = status.auto_renew === true;
                this.renewalDate = this.formatDate(status.paid_until);
                // Explicit null preserves perpetual access. Only an absent field uses the legacy date.
                this.accessDate = this.formatDate(Object.prototype.hasOwnProperty.call(status, 'access_until')
                    ? status.access_until : status.paid_until);
                this.subscriptionDate = this.formatDate(status.subscription_paid_until);
                this.billing.state = null;
                if (this.billingProvider === 'stripe') {
                    await this.billing.load();
                }
                this.loading = false;
                if (this.canPurchase && !this.checkoutCompleted && !this.needsSupport) {
                    await this.loadOffers();
                } else {
                    this.offers = [];
                }
            } catch (error) {
                console.error('[premiumSubscription] could not load subscription status', error);

                if (preserveOnError) {
                    return;
                }

                // The card must not keep asserting a subscription it can no
                // longer confirm. A reader reaching this point may have just
                // cancelled in the portal, and "Active, renews on 1 August"
                // would be a false statement about their money.
                this.resetSubscription();
                this.errorMessage = messages.loadError;
            } finally {
                this.loading = false;
            }
        },

        /** Stripe renewal claims require provider billing facts, not an entitlement expiry. */
        get renewalLine() {
            if (this.billingProvider === PROVIDER_GOOGLE) {
                return '';
            }
            if (this.billingProvider === 'stripe') {
                const billing = this.billing.requestToRetry || this.billing.working
                    ? { state: 'pending' } : this.billingStatus || { state: 'unknown' };
                return this.billingLine(billing);
            }
            const isStore = [PROVIDER_APPLE, PROVIDER_GOOGLE].includes(this.billingProvider);
            // The billing issue notice replaces a renewal claim the failed payment contradicts.
            if (isStore && this.billingIssue) {
                return '';
            }
            const date = isStore ? this.subscriptionDate : this.renewalDate;
            if (!date) {
                return isStore ? messages.billing.unknown || '' : '';
            }

            const template = this.autoRenew ? messages.renewsOn : messages.expiresOn;

            return template.replace('%date%', date);
        },

        get accessLine() {
            const settled = !this.billing.requestToRetry && !this.billing.working;
            if (this.billingProvider === 'stripe' && settled && this.repeatsEndDate(this.billingStatus, this.accessDate)) {
                return '';
            }
            return ['stripe', PROVIDER_APPLE, PROVIDER_GOOGLE].includes(this.billingProvider) && this.enabled && this.accessDate
                ? messages.accessUntil.replace('%date%', this.accessDate) : '';
        },

        /** The status chip tracks the billing facts, not only the entitlement. */
        get statusBadge() {
            // Store retry starts after the paid period ends, so it shows with access off.
            if (this.billingIssue === 'retry') {
                return 'paused';
            }
            if (!this.enabled) {
                return null;
            }
            if (this.billingIssue || this.billingStatus?.state === 'payment_required') {
                return 'payment_issue';
            }
            if (['collection_paused', 'subscription_paused'].includes(this.billingStatus?.state)) {
                return 'paused';
            }
            if (this.billingStatus?.state === 'cancellation_scheduled'
                || ([PROVIDER_APPLE, PROVIDER_GOOGLE].includes(this.billingProvider) && !this.autoRenew)) {
                return 'ending';
            }

            return 'active';
        },

        /** Store grace keeps a future paid period, which is the last day to fix the payment. */
        get billingIssueNotice() {
            if (this.billingIssue === 'retry') {
                return messages.billingRetry;
            }
            if (this.billingIssue === 'grace') {
                return this.subscriptionDate
                    ? messages.billingGraceUntil.replace('%date%', this.subscriptionDate) : messages.billingGrace;
            }

            return '';
        },

        get storeNote() {
            if (this.billingProvider === PROVIDER_APPLE) {
                return messages.appStore;
            }
            if (this.billingProvider === PROVIDER_GOOGLE) {
                return messages.playStore;
            }

            return '';
        },

        /**
         * An active member with no billing relationship and no store behind them
         * holds a membership someone granted. There is nothing for them to
         * manage, and saying so is better than ending the card on a blank space
         * where every other member sees a button.
         */
        get grantedNote() {
            if (!this.enabled || this.canManageBilling || this.storeNote) {
                return '';
            }

            return messages.granted;
        },

        /** Reachable when the entitlement lapses between page render and this read. */
        get inactiveMessage() {
            return this.checkoutCompleted ? messages.checkoutPending : messages.inactive;
        },

        /**
         * Feeds the card's one live region. The visible branches carry no ARIA
         * roles of their own, so every async transition is announced here once
         * and only once.
         */
        get statusAnnouncement() {
            if (this.loading) {
                return messages.loading;
            }
            if (this.errorMessage) {
                return this.errorMessage;
            }
            if (this.offersError) {
                return this.offersError;
            }
            // The billing controls carry no live region on this card.
            if (this.canManageBilling && this.billing.loading) {
                return messages.loading;
            }
            const billingError = this.canManageBilling ? this.billing.error : '';
            if (!this.enabled) {
                return [this.billingIssueNotice || this.inactiveMessage, this.billingProvider === 'stripe' ? this.renewalLine : '', billingError]
                    .filter(Boolean).join(' ');
            }

            const details = [this.planName, this.renewalLine, this.accessLine].filter(Boolean).join('. ');
            if (billingError) {
                return [details, billingError].join(' ');
            }
            if (this.billingIssueNotice) {
                return [details, this.billingIssueNotice].filter(Boolean).join(' ');
            }

            return [
                this.checkoutCompleted ? messages.checkoutSuccess : '',
                details,
            ].filter(Boolean).join(' ');
        },

        /**
         * Re-arms the button if the portal navigation never actually happened.
         *
         * The browser reports a committing navigation but not an abandoned one:
         * pressing Stop or Esc after location.assign() leaves the page exactly
         * as it was, with no event to say so. Holding `working` across the
         * navigation is still right, because each call mints a single-use portal
         * session that invalidates the one before it, so releasing on a plain
         * timer would let a second click cancel the URL the first navigation is
         * still loading. `pagehide` separates the two cases: it fires only when
         * the page is genuinely being torn down. If it has not fired by the time
         * the grace period is up, no navigation is coming, and a button left
         * disabled with no error and no way back is worse than a second session.
         */
        releaseIfNavigationAbandoned() {
            let leaving = false;
            const markLeaving = () => {
                leaving = true;
            };

            window.addEventListener('pagehide', markLeaving, { once: true });

            setTimeout(() => {
                window.removeEventListener('pagehide', markLeaving);
                if (!leaving) {
                    this.working = false;
                }
            }, NAVIGATION_GRACE_MS);
        },

        async loadOffers() {
            this.offersLoading = true;
            this.offersError = '';
            this.offers = [];
            try {
                const catalog = await request('/plus-offers', { method: 'GET' });
                if (!Array.isArray(catalog.offers)) {
                    throw new Error('plus-offers returned an unusable catalog');
                }
                this.offers = catalog.offers;
            } catch (error) {
                console.error('[premiumSubscription] could not load offers', error);
                this.offersError = messages.offersError;
            } finally {
                this.offersLoading = false;
            }
        },


        recordedLine(value) {
            return value ? messages.recorded.replace('%date%', this.formatDate(value)) : '';
        },


        async purchase(planId) {
            if (this.working || !this.canPurchase || this.needsSupport || this.checkoutCompleted
                || !this.offers.some(offer => offer.plan_id === planId)) {
                return;
            }
            this.working = true;
            this.errorMessage = '';
            try {
                const session = await request('/plus-checkout', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({
                    plan_id: planId,
                    immediate_access_action: 'subscribe_now_20261026_v1',
                    immediate_access_locale: strings.immediateAccessLocale,
                }) });
                const target = portalUrl(session.url);
                if (target === null) {
                    throw new Error('plus-checkout returned an unusable checkout url');
                }
                this.releaseIfNavigationAbandoned();
                window.location.assign(target);
            } catch (error) {
                this.needsSupport = error.code === 'checkout_reconciliation_required';
                this.errorMessage = this.needsSupport ? messages.reconciliation : messages.checkoutError;
                this.working = false;
                if (this.needsSupport) {
                    this.offers = [];
                }
            }
        },

        async manage() {
            if (this.working || !this.canManageBilling) {
                return;
            }

            this.working = true;
            this.errorMessage = '';

            try {
                const session = await request('/plus-manage', { method: 'POST' });

                const target = portalUrl(session.url);
                if (target === null) {
                    throw new Error('plus-manage returned an unusable portal url');
                }

                this.releaseIfNavigationAbandoned();
                window.location.assign(target);
            } catch (error) {
                console.error('[premiumSubscription] could not open the billing portal', error);
                this.errorMessage = messages.manageError;
                this.working = false;
            }
        },
    };
    return component;
}
